/**
 * BankingEntry Component
 *
 * Single labelled banking field: `InputWrapper` + `Input` + `FieldErrorList`.
 * Renders a format-validation warning (via `warning`) and external validation
 * issues (via `issues`) below the input.
 */
import * as React from 'react';
import { Input, InputWrapper } from '../../../components';
import { FieldErrorList } from '../../validation/FieldErrorList';
import type { ValidationIssue } from '../../../types';
import { IBankingDetails } from '@insio/shared-types';

export interface BankingEntryProps {
  field: keyof IBankingDetails;
  /** Element id of the input; defaults to `field`. */
  id?: string;
  label: string;
  value: string | null | undefined;
  onFieldChange: (field: keyof IBankingDetails, value: string) => void;
  placeholder?: string;
  /** Inline warning text; also drives the wrapper's error styling. */
  warning?: string | null;
  /** External validation issues rendered below the input. */
  issues?: ValidationIssue[];
  /** Uppercase the value on change (IBAN, SWIFT). */
  uppercase?: boolean;
  /** Digits-only on change (Czech payment symbols). Strips non-digits so a
   * historic malformed value still displays on open but is cleaned once edited. */
  numeric?: boolean;
  /** Max character length; sets the native attribute and truncates on change. */
  maxLength?: number;
  disabled?: boolean;
}

export const BankingEntry: React.FC<BankingEntryProps> = ({
  field,
  id = field,
  label,
  value,
  onFieldChange,
  placeholder,
  warning = null,
  issues,
  uppercase = false,
  numeric = false,
  maxLength,
  disabled = false
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let next = e.target.value;
    if (numeric) next = next.replace(/\D/g, '');
    else if (uppercase) next = next.toUpperCase();
    if (maxLength != null) next = next.slice(0, maxLength);
    onFieldChange(field, next);
  };

  return (
    <InputWrapper label={label} htmlFor={id} isError={!!warning} errorText={warning ?? undefined}>
      <Input
        id={id}
        type='text'
        inputMode={numeric ? 'numeric' : undefined}
        pattern={numeric ? '\\d*' : undefined}
        maxLength={numeric ? undefined : maxLength}
        value={value || ''}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
      />
      <FieldErrorList issues={issues} />
    </InputWrapper>
  );
};

BankingEntry.displayName = 'BankingEntry';
